import {
  createContext,
  useContext,
  useState,
  SetStateAction,
  useEffect,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { cognitoUserAttributes, useAccountContext } from "./Account";
import {
  GetUsersItems,
  DeleteItems,
  PostItem,
  GetLastUpdateGuid,
  calculateChildren,
  getImmediateChildren,
  saveImageToS3
} from "../utils/index";
import axios, { AxiosError } from "axios";
import { queuePut, queueCount, queueGetAll, queueDelete } from "../utils/OfflineQueue";
import {
  drainQueue,
  setupDrainTriggers,
  teardownDrainTriggers,
  notifyOfflineFailure,
  retryWithBackoff,
} from "../utils/OfflineDrain";

export interface cFRow {
  id: number;
  key: string | null;
  value: string | null;
}

export type itemDataFromDynamoUnaltered = {
  CustomFields: string;
  Image?: string | undefined;
  InUse?: boolean;
  InUseNote?: string | undefined;
  ItemId: string;
  ParentId: string;
  Quantity: string;
  Tags: string;
  UserId?: string | undefined;
  Name: string | undefined;
  Desc: string | undefined;
};

//Altered to change 'Parent' in dynamo table to 'ParentId'
export type itemDataFromDynamo = {
  CustomFields: cFRow[];
  Image?: string | undefined;
  InUse: boolean;
  InUseNote?: string | undefined;
  ItemId: string;
  ParentId: string;
  Quantity: string;
  Tags: string[];
  //UserId: string;
  Name?: string | undefined;
  Desc?: string | undefined;
};

// Item search results are grouped by how closely they matched, best first
export enum SearchMatchGroup {
  Exact = 1,
  Probable = 2,
  Possible = 3,
}

export const SEARCH_MATCH_GROUP_LABELS: Record<SearchMatchGroup, string> = {
  [SearchMatchGroup.Exact]: "Exact Matches",
  [SearchMatchGroup.Probable]: "Probable Matches",
  [SearchMatchGroup.Possible]: "Possible Matches",
};

type UserData = {
  itemDataFromDynamo: itemDataFromDynamo | undefined;
  setItemDataFromDynamo: React.Dispatch<
    SetStateAction<itemDataFromDynamo | undefined>
  >;
  newItemPopupOpen: boolean;
  setNewItemPopupOpen: React.Dispatch<SetStateAction<boolean>>;
  editItemPopupOpen: boolean;
  setEditItemPopupOpen: React.Dispatch<SetStateAction<boolean>>;
  itemToDelete: string;
  setItemToDelete: React.Dispatch<SetStateAction<string>>;
  itemToMove: string;
  setItemToMove: React.Dispatch<SetStateAction<string>>;
  curItemData: itemDataFromDynamo | {};
  setCurItemData: React.Dispatch<SetStateAction<itemDataFromDynamo | {}>>;
  curItemArray: itemDataFromDynamo[];
  setCurItemArray: React.Dispatch<SetStateAction<itemDataFromDynamo[]>>;
  isSearchResult: boolean;
  setIsSearchResult: React.Dispatch<SetStateAction<boolean>>;
  curParent: string;
  setCurParent: React.Dispatch<SetStateAction<string>>;
  parentToBe: string;
  setParentToBe: React.Dispatch<SetStateAction<string>>;
  itemMap: Record<string, itemDataFromDynamo>;
  setItemMap: React.Dispatch<
    SetStateAction<Record<string, itemDataFromDynamo>>
  >;
  parentMap: Record<string, Set<string>>;
  setParentMap: React.Dispatch<SetStateAction<Record<string, Set<string>>>>;
  searchMap: Record<string, Set<string>>;
  setSearchMap: React.Dispatch<SetStateAction<Record<string, Set<string>>>>;
  // ItemId -> search match group (exact / probable / possible) for the current search results
  searchMatchGroups: Record<string, SearchMatchGroup>;
  setSearchMatchGroups: React.Dispatch<SetStateAction<Record<string, SearchMatchGroup>>>;
  changeParent: (
    parentId: string | null,
    itemId: string,
    newParentId: string | null
  ) => void;
  deleteItem: (itemId: string, deleteChildren: boolean) => void;
  getItemLocation: (itemId: string) => string[];
  getItemChildren: (itemId: string) => string[];
  generateLocationStructure: (
    parentId: string,
    obj: Record<string, { children: Set<string>; level: number }>,
    level: number
  ) => void;
  moveItem: (itemId: string, location: string) => void;
  upgradedItemFormOpen: boolean;
  setupgradedItemFormOpen: React.Dispatch<SetStateAction<boolean>>;
  searchActive: boolean;
  setSearchActive: React.Dispatch<SetStateAction<boolean>>;
  logoutUser: () => void;
  recalculateCurrentArray: (
    pMap: Record<string, Set<string>>,
    parentId: string,
    iMap: Record<string, itemDataFromDynamo>,
    searchActiveVal?: boolean
  ) => void;
  regenerateCurrentArray: () => void;
  itemSearchInput: string;
  setItemSearchInput: React.Dispatch<SetStateAction<string>>;
  itemSearchValue: string;
  setItemSearchValue: React.Dispatch<SetStateAction<string>>;
  locationSearchInput: string;
  setLocationSearchInput: React.Dispatch<SetStateAction<string>>;
  locationSearchValue: string;
  setLocationSearchValue: React.Dispatch<SetStateAction<string>>;
  searchOpen: boolean;
  setSearchOpen: React.Dispatch<SetStateAction<boolean>>;
  popupMessage: string;
  setPopupMessage: React.Dispatch<SetStateAction<string>>;
  displayNav: boolean;
  setDisplayNav: React.Dispatch<SetStateAction<boolean>>;
  displayNavLinks: boolean;
  setDisplayNavLinks: React.Dispatch<SetStateAction<boolean>>;
  itemToMoveOrUse: string;
  setItemToMoveOrUse: React.Dispatch<SetStateAction<string>>;
  checkoutPopupOpen: boolean;
  setCheckoutPopupOpen: React.Dispatch<SetStateAction<boolean>>;
  subscriptionExpired: boolean;
  setSubscriptionExpired: React.Dispatch<SetStateAction<boolean>>;
  subscriptionEndDate: string;
  setSubscriptionEndDate: React.Dispatch<SetStateAction<string>>;
  subscriptionStatus: string;
  setSubscriptionStatus: React.Dispatch<SetStateAction<string>>;
  subscriptionCancelAtPeriodEnd: boolean;
  setSubscriptionCancelAtPeriodEnd: React.Dispatch<SetStateAction<boolean>>;
  bulkWorkflow: boolean;
  setBulkWorkflow: React.Dispatch<SetStateAction<boolean>>;
  SaveItem: (
    itemData: itemDataFromDynamo,
    uuid: string,
    close: boolean,
    fileUrlMainImage: string,
    userId: string,
    recalcCurrentArray: boolean,
    fileUrlLargeImage?: string
  ) => Promise<number>;
  searchItemsInUse: boolean;
  setSearchItemsInUse: React.Dispatch<SetStateAction<boolean>>;
  CancelSearch: () => void;
  isLoading: string | false;
  setIsLoading: React.Dispatch<SetStateAction<string | false>>;
  itemsInView: number;
  setItemsInView: React.Dispatch<SetStateAction<number>>;
  multiSelectMode: boolean;
  setMultiSelectMode: React.Dispatch<SetStateAction<boolean>>;
  selectedItemsToMove: string[];
  setSelectedItemsToMove: React.Dispatch<SetStateAction<string[]>>;
  pendingQueueCount: number;
  offlineFailureCount: number | null;
  setOfflineFailureCount: React.Dispatch<SetStateAction<number | null>>;
  offlineSyncedCount: number | null;
  setOfflineSyncedCount: React.Dispatch<SetStateAction<number | null>>;
  pendingPreviews: Record<string, string>;
  syncAvailable: boolean;
  refreshFromRemote: () => Promise<void>;
};

const UserDataContext = createContext<UserData | null>(null);

const MY_ITEMS_ROUTES = ["/myitems", "/success", "/cancel"];

const UserData = (props: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [itemDataFromDynamo, setItemDataFromDynamo] =
    useState<itemDataFromDynamo>();
  const [newItemPopupOpen, setNewItemPopupOpen] = useState(false);
  const [editItemPopupOpen, setEditItemPopupOpen] = useState(false);
  const [upgradedItemFormOpen, setupgradedItemFormOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string>("");
  const [itemToMove, setItemToMove] = useState<string>("");
  const [itemToMoveOrUse, setItemToMoveOrUse] = useState<string>("");
  const [curItemData, setCurItemData] = useState<itemDataFromDynamo | {}>({});
  const [curItemArray, setCurItemArray] = useState<itemDataFromDynamo[]>([]);
  const [isSearchResult, setIsSearchResult] = useState<boolean>(false);
  const [curParent, setCurParent] = useState("MASTER");
  // getUsers (below) only gets re-created when loggedInUserCognitoIdToken changes,
  // so its closure would otherwise freeze curParent at whatever it was on that one
  // render (typically "MASTER", right after login/impersonation-reload). This ref
  // lets it always read the current location instead of resetting the view to
  // MASTER any time a stale-data refresh fires mid-session.
  const curParentRef = useRef(curParent);
  useEffect(() => { curParentRef.current = curParent; }, [curParent]);
  const [parentToBe, setParentToBe] = useState("MASTER");
  const [itemMap, setItemMap] = useState<Record<string, itemDataFromDynamo>>(
    {}
  );
  const [parentMap, setParentMap] = useState<Record<string, Set<string>>>({});
  const [searchMap, setSearchMap] = useState<Record<string, Set<string>>>({});
  const [searchMatchGroups, setSearchMatchGroups] = useState<
    Record<string, SearchMatchGroup>
  >({});
  const [searchActive, setSearchActive] = useState(false);
  const [itemSearchInput, setItemSearchInput] = useState("");
  const [itemSearchValue, setItemSearchValue] = useState("");
  const [locationSearchInput, setLocationSearchInput] = useState("");
  const [locationSearchValue, setLocationSearchValue] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [displayNav, setDisplayNav] = useState(true);
  const [displayNavLinks, setDisplayNavLinks] = useState(true);
  const [searchItemsInUse, setSearchItemsInUse] = useState(false);
  const [isLoading, setIsLoading] = useState<string | false>(false);
  const [checkoutPopupOpen, setCheckoutPopupOpen] = useState(false);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [subscriptionCancelAtPeriodEnd, setSubscriptionCancelAtPeriodEnd] =
    useState(false);
  const [itemsInView, setItemsInView] = useState(
    parseInt(process.env.REACT_APP_ITEM_VIEW_INTERVAL!)
  );
  const [bulkWorkflow, setBulkWorkflow] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedItemsToMove, setSelectedItemsToMove] = useState<string[]>([]);
  const [pendingQueueCount, setPendingQueueCount] = useState(0);
  const [offlineFailureCount, setOfflineFailureCount] = useState<number | null>(null);
  const [offlineSyncedCount, setOfflineSyncedCount] = useState<number | null>(null);
  const [pendingPreviews, setPendingPreviews] = useState<Record<string, string>>({});
  const [syncAvailable, setSyncAvailable] = useState(false);
  const {
    loggedInUserCognitoIdToken,
    userCognitoAttrubutes,
    logout,
    setLoggedInUserCognitoIdToken,
    getRefreshedToken,
    dataUserId,
    actingUserId,
    impersonatedUserId,
  } = useAccountContext();

  // Keep a stable ref to logoutUser so the session-expired event handler always
  // calls the latest version without needing it as an effect dependency.
  const logoutUserRef = useRef<() => void>(() => {});
  useEffect(() => { logoutUserRef.current = logoutUser; });
  useEffect(() => {
    const handler = () => logoutUserRef.current();
    window.addEventListener("cognitoSessionExpired", handler);
    return () => window.removeEventListener("cognitoSessionExpired", handler);
  }, []);

  // lastUpdateGuid tracks the most recent write GUID so we can detect changes from
  // other devices/users before writing. Using refs avoids stale-closure issues in
  // the polling interval without triggering unnecessary re-renders.
  const lastUpdateGuidRef = useRef<string>("");
  const checkInProgressRef = useRef(false);
  const refreshItemsFnRef = useRef<() => Promise<void>>(async () => {});
  // Stable ref so the polling interval always calls the latest pollForSync.
  const pollForSyncRef = useRef<() => Promise<void>>(async () => {});
  useEffect(() => { pollForSyncRef.current = pollForSync; });

  async function checkAndRefreshIfNeeded(): Promise<void> {
    // The LastUpdateGuid endpoint derives its user from the JWT, not the
    // x-data-user-id header, so while impersonating this would compare against
    // the acting admin's own guid rather than the impersonated user's — skip
    // it entirely rather than act on a misleading signal.
    if (impersonatedUserId || checkInProgressRef.current || !navigator.onLine || !dataUserId) return;
    checkInProgressRef.current = true;
    try {
      const dbGuid = await GetLastUpdateGuid(getRefreshedToken, dataUserId);
      if (dbGuid !== null && dbGuid !== lastUpdateGuidRef.current) {
        await refreshItemsFnRef.current();
        setSyncAvailable(false); // data is already fresh — dismiss any pending banner
      }
    } finally {
      checkInProgressRef.current = false;
    }
  }

  async function pollForSync(): Promise<void> {
    // Skip while backgrounded/hidden (no point polling a tab nobody's looking
    // at — and on iOS PWAs, JS is suspended in the background anyway) or once
    // a refresh is already pending — no need to re-check until the user acts
    // on the banner that's already showing. A visibilitychange listener below
    // fires an immediate check the moment the page becomes visible again.
    if (impersonatedUserId || !navigator.onLine || document.hidden || !dataUserId || checkInProgressRef.current || syncAvailable) return;
    try {
      const dbGuid = await GetLastUpdateGuid(getRefreshedToken, dataUserId);
      if (dbGuid !== null && dbGuid !== lastUpdateGuidRef.current) {
        setSyncAvailable(true);
      }
    } catch {
      // silently ignore polling failures
    }
  }

  async function refreshFromRemote(): Promise<void> {
    setSyncAvailable(false);
    await refreshItemsFnRef.current();
  }

  // Whenever a My Items route loads, silently check for and pull in any pending
  // update before the user sees stale data — this way the "updates available"
  // banner never has to show for that load (it only shows if an update lands
  // while already sitting on the page).
  useEffect(() => {
    if (MY_ITEMS_ROUTES.includes(location.pathname.toLowerCase())) {
      checkAndRefreshIfNeeded();
    }
  }, [location.pathname]);

  //When the Cognito User Id changes we need to get the data from dynamo and
  //reset the ItemMap, ParentMap, and SearchMap
  useEffect(() => {
    async function getUsers() {
      if (
        loggedInUserCognitoIdToken !== null &&
        loggedInUserCognitoIdToken !== undefined &&
        loggedInUserCognitoIdToken !== ""
      ) {
        try {
          setIsLoading("Fetching Items...");
          // Use dataUserId from Account context (handles impersonation automatically)
          var userItemInfo = await GetUsersItems(
            getRefreshedToken,
            actingUserId,
            dataUserId
          );
          if (userItemInfo.logUserOut) {
            logoutUser();
          } else {
            setSubscriptionStatus(userItemInfo.subscriptionStatus);
            setSubscriptionEndDate(userItemInfo.subscriptionEndDate);
            setSubscriptionExpired(userItemInfo.subscriptionExpired);
            setSubscriptionCancelAtPeriodEnd(
              userItemInfo.subscriptionCancelAtPeriodEnd
            );
            if (!userItemInfo.errorGettingData) {
              lastUpdateGuidRef.current = userItemInfo.lastUpdateGuid ?? "";
              recalculateCurrentArray(
                userItemInfo.parentHashMap,
                curParentRef.current,
                userItemInfo.itemHashMap
              );
              setItemMap(userItemInfo.itemHashMap);
              setParentMap(userItemInfo.parentHashMap);
              setSearchMap(userItemInfo.searchHashMap);
            } else {
              setItemMap({});
              setParentMap({});
              setSearchMap({});
            }
          }
        } catch (e) {
          setItemMap({});
          setParentMap({});
          setSearchMap({});
        } finally {
          setIsLoading(false);
          // setTimeout(() => {
          //   setIsLoading(false);
          // }, Math.max(Date.now() - startTime, 0));
        }
      }
    }
    refreshItemsFnRef.current = getUsers;
    getUsers();
  }, [loggedInUserCognitoIdToken]);

  // Wire up offline sync when logged in: persist storage, load initial queue count,
  // set up drain triggers (online event, visibilitychange, 45s interval), and
  // attempt an immediate drain in case items queued during a previous offline session.
  useEffect(() => {
    if (loggedInUserCognitoIdToken && loggedInUserCognitoIdToken !== "") {
      if (navigator.storage?.persist) navigator.storage.persist();
      queueCount().then(setPendingQueueCount);
      setupDrainTriggers({
        onCountChange: setPendingQueueCount,
        onSyncedItems: setOfflineSyncedCount,
        onOfflineFailure: setOfflineFailureCount,
        onItemSynced: (itemId, s3ImageUrl, payload) => {
          const resolvedImage = s3ImageUrl ?? payload?.Image;
          setItemMap(prev => ({
            ...prev,
            [itemId]: prev[itemId]
              ? { ...prev[itemId], Image: resolvedImage }
              : { ...payload, Image: resolvedImage },
          }));
          setParentMap(prev => {
            const parentId = payload?.ParentId ?? "MASTER";
            const siblings = new Set(prev[parentId] ?? []);
            siblings.add(itemId);
            return { ...prev, [parentId]: siblings };
          });
          setCurItemArray(prev => prev.map(i =>
            i.ItemId === itemId ? { ...i, Image: resolvedImage } : i
          ));
          setPendingPreviews(prev => {
            const next = { ...prev };
            delete next[itemId];
            return next;
          });
        },
      });
      drainQueue();
      const syncInterval = setInterval(() => pollForSyncRef.current(), 120 * 1000);
      // Fire an immediate check as soon as the page/PWA becomes visible again,
      // rather than waiting for the next scheduled interval tick.
      const onVisibilityChange = () => {
        if (!document.hidden) pollForSyncRef.current();
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      return () => {
        teardownDrainTriggers();
        clearInterval(syncInterval);
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
    }
  }, [loggedInUserCognitoIdToken]);

  //Routes delete item to based on if children should be deleted
  async function deleteItem(itemId: string, deleteChildren: boolean) {
    var itemIds: string[] = [];
    if (deleteChildren) {
      itemIds = calculateChildren(parentMap, itemId);
      itemIds.push(itemId);
    } else {
      itemIds = [itemId];
    }

    if (!navigator.onLine) {
      // Offline queueing is disabled during impersonation (drain is also paused for the
      // whole session — see OfflineDrain.ts) — bail out before any optimistic mutation
      // rather than leaving a change queued that won't sync until impersonation ends.
      if (impersonatedUserId) {
        alert(
          "Deleting requires an internet connection while impersonating. Offline queueing is disabled during impersonation. Please check your connection and try again."
        );
        return;
      }
      // Optimistically remove the items from local state
      const item = itemMap[itemId];
      const parentId = item?.ParentId ?? "MASTER";
      const parent = itemMap[parentId];
      const newItemMap = deleteItemsFromItemMap(itemIds, parentId);
      const newParentMap = recalculateParentMap(newItemMap);
      let newCurParent = parentId;
      if (newParentMap[parentId] == null && parentId !== "MASTER") {
        newCurParent = parent?.ParentId ?? "MASTER";
      }
      recalculateCurrentArray(newParentMap, newCurParent, newItemMap);
      setItemToDelete("");
      setupgradedItemFormOpen(false);

      // Remove any items from the save queue that were created offline and
      // never reached the DB — those don't need a network delete
      const pendingSaves = await queueGetAll();
      const pendingSaveIds = new Set(pendingSaves.map(p => p.localId));
      for (const id of itemIds) {
        if (pendingSaveIds.has(id)) {
          await queueDelete(id);
        }
      }

      // Queue a DELETE for any items that already exist in the DB
      const idsToDeleteFromDB = itemIds.filter(id => !pendingSaveIds.has(id));
      if (idsToDeleteFromDB.length > 0) {
        await queuePut({
          localId: `delete_${itemId}`,
          method: "DELETE",
          itemIds: idsToDeleteFromDB,
          userId: dataUserId,
          payload: {} as itemDataFromDynamo,
          thumbnailDataUrl: "",
          createdAt: Date.now(),
          attempts: 0,
        });
      }
      return;
    }

    await checkAndRefreshIfNeeded();
    const newUpdateGuid = crypto.randomUUID();

    var result;
    try {
      if (deleteChildren) {
        result = await deleteMultipleItems(itemIds, newUpdateGuid);
      } else {
        result = await deleteSingleItem(itemIds, newUpdateGuid);
      }
      recalculateCurrentArray(
        result.parentHashMap,
        result.parentId,
        result.itemHashMap
      );
    } catch (error) {
      console.error("Error in function deleteItem - ", error);
      throw error;
    }
  }

  async function deleteSingleItem(itemIds: string[], newUpdateGuid: string): Promise<{
    itemHashMap: Record<string, itemDataFromDynamo>;
    parentHashMap: Record<string, Set<string>>;
    toDelete: never[];
    parentId: string;
  }> {
    const item: itemDataFromDynamo = itemMap[itemIds[0]];
    // May be undefined if the parent was itself deleted without its
    // children being cleaned up (orphaned data) - callers fall back to
    // MASTER in that case rather than assuming this exists.
    const parent: itemDataFromDynamo | undefined = itemMap[item.ParentId!];
    var itemHashMap: Record<string, itemDataFromDynamo> = {};
    var parentHashMap: Record<string, Set<string>> = {};
    var parentId: string = item.ParentId;
    setIsLoading("Deleting...");

    //Delete the data from Dynamo
    await DeleteItems(
      itemIds,
      actingUserId,
      subscriptionExpired,
      dataUserId,
      getRefreshedToken,
      newUpdateGuid
    )
      .then(async (res: any) => {
        lastUpdateGuidRef.current = newUpdateGuid;
        // Skip while impersonating — see note on the save flow's guid check.
        const dbGuidAfterDelete = impersonatedUserId ? null : await GetLastUpdateGuid(getRefreshedToken, dataUserId);
        if (dbGuidAfterDelete !== null && dbGuidAfterDelete !== newUpdateGuid) {
          await refreshItemsFnRef.current();
        }
        //Update the parent map
        itemHashMap = deleteItemsFromItemMap(itemIds, item.ParentId!);
        //Recalculate the parent map with the new Item Hash Map
        parentHashMap = recalculateParentMap(itemHashMap);
        //Update children parentId value in database
        var children = getImmediateChildren(parentMap, item.ItemId);
        children.forEach(async (child) => {
          await moveItem(child, parentId, parentHashMap, itemHashMap);
        });

        //If parent now contains no children then curParent state needs to be set to the parent of the deleted item's parent
        parentId = item.ParentId!;
        if (
          parentHashMap[item.ParentId!] == null &&
          item.ParentId! !== "MASTER"
        ) {
          parentId = parent?.ParentId ?? "MASTER";
        }
        setItemToDelete("");
        setupgradedItemFormOpen(false);
      })
      .catch((reason: any) => {
        itemHashMap = itemMap;
        parentHashMap = parentMap;
        alert(
          "Error deleting item from database. Please check your internet connection and try again. Contact Admin@WheresOur.com if the issue persists."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
    return { itemHashMap, parentHashMap, toDelete: [], parentId };
  }

  function changeParent(
    parentId: string | null,
    itemId: string,
    newParentId: string | null
  ) {
    //Create new map to replace parent
    var parentHashMap: Record<string, Set<string>> = {};
    //If parentid not in parent map it needs to be added
    if (newParentId != null && parentMap[newParentId] == null) {
      parentHashMap[newParentId] = new Set<string>();
    }
    for (const key in parentMap) {
      //delete the item from the current parent
      if (key === parentId) {
        //Add the children of the item to the parent
        for (const s in parentMap[itemId]) {
          parentMap[key].add(s);
        }
        parentMap[key].delete(itemId);

        if (parentMap[key].size === 0) {
          //If item is no longer a parent we want to remove it from the Map
          continue;
        }
      }
      //Add item to set of new parent
      if (key === newParentId) {
        parentMap[key].add(itemId);
      }
      parentHashMap[key] = parentMap[key];
    }
    return parentHashMap;
  }

  function deleteItemsFromItemMap(
    itemIds: string[],
    parentId: string
  ): Record<string, itemDataFromDynamo> {
    //get all children
    var children: Set<string> = parentMap[itemIds[0]];
    //Create new map to replace current
    var itemHashMap: Record<string, itemDataFromDynamo> = {};

    for (const key in itemMap) {
      //Continue the for loop when you hit the itemId
      if (itemIds.includes(key)) {
        continue;
      }
      //Update the parent id of children to the item's parent
      if (children != null && children.has(key)) {
        itemMap[key].ParentId = parentId;
      }
      itemHashMap[key] = itemMap[key];
    }
    return itemHashMap;
  }

  async function deleteMultipleItems(
    itemIds: string[],
    newUpdateGuid: string
  ): Promise<{
    itemHashMap: Record<string, itemDataFromDynamo>;
    parentHashMap: Record<string, Set<string>>;
    parentId: string;
  }> {
    //Default to the current maps unchanged - only replaced below once the
    //delete is confirmed to have succeeded server-side
    var itemHashMap: Record<string, itemDataFromDynamo> = itemMap;
    var parentHashMap: Record<string, Set<string>> = parentMap;
    //The item being deleted is the last one added to the array so get the parent for that item to set the new current parent
    var parentId: string = itemMap[itemIds[itemIds.length - 1]].ParentId!;
    setIsLoading("Deleting...");

    //Delete the data from Dynamo - wait for confirmation before touching
    //local state, so a failed/partial delete of a large subtree doesn't
    //leave the UI showing items as gone that are still in the database
    await DeleteItems(
      itemIds,
      actingUserId,
      subscriptionExpired,
      dataUserId,
      getRefreshedToken,
      newUpdateGuid
    )
      .then(async (res: any) => {
        lastUpdateGuidRef.current = newUpdateGuid;
        // Skip while impersonating — see note on the save flow's guid check.
        const dbGuidAfterDelete = impersonatedUserId ? null : await GetLastUpdateGuid(getRefreshedToken, dataUserId);
        if (dbGuidAfterDelete !== null && dbGuidAfterDelete !== newUpdateGuid) {
          await refreshItemsFnRef.current();
        }
        //loop over itemMap and skip ones that need to be deleted
        const newItemHashMap: Record<string, itemDataFromDynamo> = {};
        for (const key in itemMap) {
          if (!itemIds.includes(key)) {
            newItemHashMap[key] = itemMap[key];
          }
        }
        itemHashMap = newItemHashMap;
        parentHashMap = recalculateParentMap(itemHashMap);
      })
      .catch((reason: any) => {
        itemHashMap = itemMap;
        parentHashMap = parentMap;
        alert(
          "Error deleting item from database. Please check your internet connection and try again. Contact Admin@WheresOur.com if the issue persists."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });

    //pass item and parent maps back along with the list of items to delete
    return { itemHashMap, parentHashMap, parentId };
  }

  function recalculateParentMap(
    i: Record<string, itemDataFromDynamo> = itemMap
  ) {
    //Create new map to replace current
    var parentHashMap: Record<string, Set<string>> = {};

    for (const key in i) {
      //Recalculate Parent Map
      if (i[key].ParentId != null) {
        if (parentHashMap[i[key].ParentId!] == null) {
          parentHashMap[i[key].ParentId!] = new Set<string>([i[key].ItemId!]);
        } else {
          parentHashMap[i[key].ParentId!].add(i[key].ItemId!);
        }
      }
    }
    return parentHashMap;
  }

  //Recursive function to get the location of an item
  function getItemLocation(itemId: string, arr: string[] = []): string[] {
    arr.unshift(itemId);
    //var a = itemMap[itemId].ParentId;
    if (itemId !== "MASTER") {
      // Every item is expected to have a ParentId (ultimately terminating at
      // "MASTER"). If one doesn't, log it so the bad data can be tracked
      // down, and stop here instead of recursing into itemMap[undefined],
      // which throws.
      if (itemMap[itemId] == null || itemMap[itemId].ParentId == null) {
        console.log(
          "getItemLocation: item has no ParentId",
          itemId,
          itemMap[itemId]
        );
      } else {
        //a += "|" + getItemLocation(itemMap[itemId].ParentId!);
        getItemLocation(itemMap[itemId].ParentId!, arr);
      }
    }
    return arr;
  }

  //Recursive function to get all children of an item
  function getItemChildren(itemId: string, arr: string[] = []): string[] {
    //Add all the item Ids to the list
    if (parentMap[itemId] != null) {
      parentMap[itemId].forEach((s) => {
        arr.push(s);
        //Check if item is a parent and if so call getItemChildren on it
        if (parentMap[s] != null) {
          getItemChildren(s, arr);
        }
      });
    }
    return arr;
  }

  //Move item to another location
  async function moveItem(
    itemId: string,
    location: string,
    pMap?: Record<string, Set<string>>,
    iMap?: Record<string, itemDataFromDynamo>
  ) {
    if (iMap == null) {
      iMap = itemMap;
    }
    // Only run pre-save check when called as a user action (pMap == null).
    // Internal calls from deleteSingleItem pass pMap and should not re-check.
    let newUpdateGuid: string | undefined;
    if (pMap == null) {
      await checkAndRefreshIfNeeded();
      newUpdateGuid = crypto.randomUUID();
    }
    let itemData: itemDataFromDynamo = iMap[itemId];
    itemData.ParentId = location;
    await PostItem(
      itemData,
      actingUserId,
      subscriptionExpired,
      dataUserId,
      getRefreshedToken,
      newUpdateGuid
    )
      .then(() => {
        if (newUpdateGuid) lastUpdateGuidRef.current = newUpdateGuid;
        iMap![itemId].ParentId = location;
        var itemHashMap: Record<string, itemDataFromDynamo> = {};
        for (const key in iMap) {
          itemHashMap[key] = iMap![key];
        }
        var parentHashMap = recalculateParentMap(itemHashMap);
        //Call update database

        recalculateCurrentArray(
          parentHashMap!,
          itemHashMap[itemId].ParentId!,
          iMap!
        );
        //setItemMap(itemHashMap);
        //setParentMap(parentHashMap);
        //setCurParent(itemHashMap[itemId].ParentId!);
      })
      .catch((error) => {
        if (error == 0) {
          alert(
            "Error saving data. This issue can be a result of lost internet connection. Please check your connection and try again."
          );
        } else {
          alert(
            "Error moving item. Please try again and contact Admin@WheresOur.com if the issue persists. Error code = " +
              error
          );
        }
      });
  }

  //Move item to another location
  function generateLocationStructure(
    parentId: string = "MASTER",
    obj: Record<string, { children: Set<string>; level: number }> = {},
    level: number = 1
  ) {
    //Get children of parent
    var children = parentMap[parentId];
    //If parent has children add to object
    obj[parentId] = { children, level };

    if (children != null) {
      level++;
      children.forEach((child) => {
        generateLocationStructure(child, obj, level);
      });
    }
    return obj;
  }

  function recalculateCurrentArray(
    pMap: Record<string, Set<string>>,
    parentId: string,
    iMap: Record<string, itemDataFromDynamo>,
    searchActiveVal?: boolean
  ) {
    //setItemsInView(parseInt(process.env.REACT_APP_ITEM_VIEW_INTERVAL!));
    if (searchActiveVal == null) {
      searchActiveVal = searchActive;
    }
    var itemArray: itemDataFromDynamo[] = [];
    //Only add items to the array if there are items to get
    if (pMap[parentId] != null) {
      pMap[parentId].forEach((item) => {
        itemArray.push(iMap[item]);
      });
    }
    if (searchActiveVal === false) {
      setCurItemArray(
        itemArray.sort((a, b) => {
          const aName = a.Name ?? "";
          const bName = b.Name ?? "";
          return aName.localeCompare(bName, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        })
      );
    } else {
      // Keep the existing search result order (results are ranked by match tier)
      itemArray = [];
      curItemArray.forEach((item) => {
        const updatedItem = iMap[item.ItemId];
        if (updatedItem != null) {
          itemArray.push(updatedItem);
        }
      });
      setCurItemArray(itemArray);
    }
    setCurParent(parentId);
    setParentMap(pMap);
    setItemMap(iMap);
    //window.scrollTo(0, 0);
  }

  //Updates the item data within the current array without changing
  function regenerateCurrentArray() {
    var itemArray: itemDataFromDynamo[] = [];
    curItemArray.map((item) => {
      itemArray.push(itemMap[item.ItemId]);
    });
    console.log(itemArray);
    setCurItemArray(itemArray);
  }

  const SaveItem = (
    itemData: itemDataFromDynamo,
    uuid: string,
    close: boolean,
    fileUrlMainImage: string,
    userId: string,
    recalcCurrentArray: boolean,
    fileUrlLargeImage?: string
  ): Promise<number> => {
    // 1. Apply optimistic update immediately so the user sees their change right away,
    //    regardless of connectivity.
    //    When a new image is being uploaded, keep the item's current image (old photo for
    //    edits, undefined for new items) rather than setting the S3 URL up-front. S3 returns
    //    403 for non-existent objects in private buckets, <img> won't retry the same src
    //    after a failure, and React 18 blocks data: URLs in src. The real S3 URL is written
    //    to state once the upload succeeds (see step 4 below).
    const hasImage = fileUrlMainImage != null && fileUrlMainImage !== "";
    const s3ImageUrl = itemData.Image?.replace("{CLIENTID}", dataUserId);
    const optimisticImage = hasImage ? itemMap[itemData.ItemId]?.Image : s3ImageUrl;
    const optimisticItemData = { ...itemData, Image: optimisticImage };

    if (itemMap[optimisticItemData.ItemId] != null) {
      HandleSuccessfulHttpPut(optimisticItemData, recalcCurrentArray);
    } else {
      HandleSuccessfulHttpPost(optimisticItemData, recalcCurrentArray);
    }

    var searchHashMap: Record<string, Set<string>> = {};
    for (const key in searchMap) {
      searchHashMap[key.toLowerCase()] = searchMap[key.toLowerCase()];
      searchHashMap[key.toLowerCase()].delete(optimisticItemData.ItemId);
    }
    if (optimisticItemData.Name != null && optimisticItemData.Name !== "") {
      if (searchHashMap[optimisticItemData.Name.toLowerCase()]) {
        searchHashMap[optimisticItemData.Name.toLowerCase()].add(optimisticItemData.ItemId);
      } else {
        searchHashMap[optimisticItemData.Name.toLowerCase()] = new Set<string>().add(optimisticItemData.ItemId);
      }
    }
    if (optimisticItemData.Tags.length > 0) {
      optimisticItemData.Tags.forEach((value) => {
        if (searchHashMap[value.toLowerCase()]) {
          searchHashMap[value.toLowerCase()].add(optimisticItemData.ItemId);
        } else {
          searchHashMap[value.toLowerCase()] = new Set<string>().add(optimisticItemData.ItemId);
        }
      });
    }
    setSearchMap(searchHashMap);

    if (close) {
      setCurItemData({});
      setupgradedItemFormOpen(false);
      setBulkWorkflow(false);
    } else {
      setCurItemData({});
      window.scrollTo(0, 0);
      setItemsInView(parseInt(process.env.REACT_APP_ITEM_VIEW_INTERVAL!));
    }

    return new Promise(async (resolve) => {
      if (subscriptionExpired) {
        resolve(1);
        return;
      }

      // 2. Cheap check (just a guid comparison, not a data fetch) for whether
      //    local state already predates the server before we write. We can't
      //    rely solely on the post-save check below for this: once we write,
      //    our own guid becomes "latest" and would mask an earlier change we
      //    never actually pulled in. We don't refresh here, though — that
      //    would wipe the optimistic update above — we just remember to
      //    refresh after our own write lands, when it's always safe to do so.
      // The LastUpdateGuid endpoint derives its user from the JWT, not the
      // x-data-user-id header, so while impersonating it would check the acting
      // admin's own guid rather than the impersonated user's — skip the check
      // entirely rather than act on a misleading signal.
      let staleBeforeSave = false;
      if (!impersonatedUserId && navigator.onLine && dataUserId) {
        const dbGuidBeforeSave = await GetLastUpdateGuid(getRefreshedToken, dataUserId);
        staleBeforeSave = dbGuidBeforeSave !== null && dbGuidBeforeSave !== lastUpdateGuidRef.current;
      }
      const newUpdateGuid = crypto.randomUUID();
      await drainQueue();

      let mainImageUrl = "";
      let largeImageUrl = "";

      try {
        // 3. POST + thumbnail: single retry unit. If either fails, the whole unit retries
        //    (POST re-runs to get fresh presigned URLs). Auth/subscription errors not retried.
        await retryWithBackoff(
          async () => {
            const res = await PostItem(itemData, userId, subscriptionExpired, dataUserId, getRefreshedToken, newUpdateGuid) as any;
            mainImageUrl = res.data?.mainImageUrl ?? "";
            largeImageUrl = res.data?.largeImageUrl ?? "";

            if (hasImage && mainImageUrl) {
              const resp = await saveImageToS3(
                mainImageUrl, fileUrlMainImage, uuid, "mainimg.jpeg",
                userId, userCognitoAttrubutes, await getRefreshedToken()
              );
              if (resp < 200 || resp >= 300) throw new Error(`Thumbnail upload failed: ${resp}`);
            }
          },
          [1500, 3000],
          (err) => err !== 401 && err !== 599
        );

        lastUpdateGuidRef.current = newUpdateGuid;
        // Skip the post-save check too while impersonating, for the same reason
        // as the pre-save check above.
        const dbGuidAfterSave = impersonatedUserId ? null : await GetLastUpdateGuid(getRefreshedToken, dataUserId);
        // Refresh if local was already stale before this save (staleBeforeSave —
        // our own write's guid would otherwise mask that unpulled change) or if
        // another write landed during our own save (dbGuidAfterSave mismatch).
        // Either way this is safe now: our item is already persisted server-side.
        if (staleBeforeSave || (dbGuidAfterSave !== null && dbGuidAfterSave !== newUpdateGuid)) {
          await refreshItemsFnRef.current();
        }
        // Our write has landed (and any other device's changes have just been
        // pulled in above if needed), so any pending refresh banner is now moot.
        setSyncAvailable(false);

        // 4. Thumbnail is now in S3 — write the real URL into both itemMap and curItemArray.
        //    Both need updating: itemMap is the source of truth, curItemArray is what the
        //    card view renders from and is only rebuilt by recalculateCurrentArray.
        //    Functional updates ensure we read current state, not the stale closure value.
        if (hasImage && s3ImageUrl) {
          setItemMap(prev => ({
            ...prev,
            [itemData.ItemId]: { ...(prev[itemData.ItemId] ?? optimisticItemData), Image: s3ImageUrl },
          }));
          setCurItemArray(prev => prev.map(i =>
            i.ItemId === itemData.ItemId ? { ...i, Image: s3ImageUrl } : i
          ));
        }

        // 6. Resolve immediately — UI doesn't wait for large image.
        resolve(hasImage && mainImageUrl ? 0 : 2);

        // 7. Large image: independent background retry using the presigned URL already in hand.
        //    3 attempts with longer backoff and a 30s timeout per attempt (safe since it's
        //    background). If all attempts fail the item stays thumbnail-only permanently.
        if (fileUrlLargeImage && largeImageUrl) {
          const capturedLargeUrl = largeImageUrl;
          (async () => {
            await retryWithBackoff(
              async () => {
                const resp = await saveImageToS3(
                  capturedLargeUrl, fileUrlLargeImage, uuid, "largeimg.jpeg",
                  userId, userCognitoAttrubutes, await getRefreshedToken(), 30000
                );
                if (resp < 200 || resp >= 300) throw new Error(`Large image upload failed: ${resp}`);
              },
              [3000, 8000]
            );
          })().catch(() => {});
        }
      } catch (err: any) {
        if (err === 401 || err === 599) {
          resolve(1);
          return;
        }

        // Offline queueing is disabled during impersonation (drain is also paused for the
        // whole session — see OfflineDrain.ts). The optimistic update above already applied,
        // but with nothing queued it won't be retried or synced — tell the admin now rather
        // than let it silently vanish on the next refresh.
        if (impersonatedUserId) {
          alert(
            "Error saving data while impersonating. This change was not saved and will not be retried automatically — offline queueing is disabled during impersonation. Please check your connection and try saving again."
          );
          resolve(1);
          return;
        }

        // 5. Queue for background sync — item is already visible in UI via optimistic update
        try {
          await queuePut({
            localId: itemData.ItemId,
            payload: itemData,
            thumbnailDataUrl: fileUrlMainImage ?? "",
            userId: dataUserId,
            createdAt: Date.now(),
            attempts: 0,
          });
          // Store local data URL so the card can show a preview while the item is queued
          if (fileUrlMainImage) {
            setPendingPreviews(prev => ({ ...prev, [itemData.ItemId]: fileUrlMainImage }));
          }
          const count = await queueCount();
          setPendingQueueCount(count);
          notifyOfflineFailure(count);
        } catch (queueErr) {
          console.error("Failed to queue item for offline sync:", queueErr);
        }
        resolve(0);
      }
    });
  };

  function HandleSuccessfulHttpPost(
    itemData: itemDataFromDynamo,
    recalcCurrentArray: boolean
  ): Record<string, itemDataFromDynamo> {
    //Update the item hashmap with the new item
    var itemHashMap: Record<string, itemDataFromDynamo> = {};
    for (const key in itemMap) {
      itemHashMap[key] = itemMap[key];
    }
    itemHashMap[itemData.ItemId] = itemData;

    var parentHashMap: Record<string, Set<string>> = {};
    for (const key in parentMap) {
      parentHashMap[key] = parentMap[key];
    }

    if (parentHashMap[itemData.ParentId!] == null) {
      parentHashMap[itemData.ParentId!] = new Set<string>();
    }
    parentHashMap[itemData.ParentId!] = parentHashMap[itemData.ParentId!].add(
      itemData.ItemId!
    );
    if (recalcCurrentArray) {
      recalculateCurrentArray(parentHashMap, curParent, itemHashMap);
    }

    setItemMap(itemHashMap);
    setParentMap(parentHashMap);
    return itemHashMap;
  }
  function HandleSuccessfulHttpPut(
    itemData: itemDataFromDynamo,
    recalcCurrentArray: boolean
  ): Record<string, itemDataFromDynamo> {
    //Update the item hashmap with the updated item data
    var itemHashMap: Record<string, itemDataFromDynamo> = {};
    for (const key in itemMap) {
      if (key === itemData.ItemId) {
        itemHashMap[key] = itemData;
      } else {
        itemHashMap[key] = itemMap[key];
      }
    }
    var parentHashMap: Record<string, Set<string>> = {};
    for (const key in parentMap) {
      parentHashMap[key] = parentMap[key];
    }
    if (parentHashMap[itemData.ParentId!] == null) {
      parentHashMap[itemData.ParentId!] = new Set<string>();
    }
    parentHashMap[itemData.ParentId!] = parentHashMap[itemData.ParentId!].add(
      itemData.ItemId!
    );
    if (recalcCurrentArray) {
      recalculateCurrentArray(parentHashMap, curParent, itemHashMap);
    }

    setItemMap(itemHashMap);
    setParentMap(parentHashMap);
    return itemHashMap;
  }

  function CancelSearch() {
    setItemSearchInput("");
    setItemSearchValue("");
    setLocationSearchInput("");
    setLocationSearchValue("");
    //recalculateCurrentArray(parentMap, curParent, itemMap, false);
    setSearchActive(false);
    setSearchItemsInUse(false);
    setSearchMatchGroups({});
    setItemsInView(parseInt(process.env.REACT_APP_ITEM_VIEW_INTERVAL!));
  }

  function logoutUser() {
    CancelSearch();
    setItemDataFromDynamo(undefined);
    setNewItemPopupOpen(false);
    setEditItemPopupOpen(false);
    setupgradedItemFormOpen(false);
    setItemToDelete("");
    setItemToMove("");
    setCurItemData({});
    setCurItemArray([]);
    setIsSearchResult(false);
    setCurParent("MASTER");
    setParentToBe("MASTER");
    setItemMap({});
    setParentMap({});
    setSearchMap({});
    setLoggedInUserCognitoIdToken("");
    setItemSearchInput("");
    setItemSearchValue("");
    setLocationSearchInput("");
    setLocationSearchValue("");
    setPendingPreviews({});
    localStorage.removeItem("CognitoIdToken");
    localStorage.removeItem("UserCognitoAttrubutes");
    localStorage.removeItem("expireTime");
    logout();
    navigate("/");
  }

  return (
    <UserDataContext.Provider
      value={{
        itemDataFromDynamo,
        setItemDataFromDynamo,
        newItemPopupOpen,
        setNewItemPopupOpen,
        curItemData,
        setCurItemData,
        curItemArray,
        setCurItemArray,
        isSearchResult,
        setIsSearchResult,
        curParent,
        setCurParent,
        itemMap,
        setItemMap,
        parentMap,
        setParentMap,
        editItemPopupOpen,
        setEditItemPopupOpen,
        searchMap,
        setSearchMap,
        searchMatchGroups,
        setSearchMatchGroups,
        parentToBe,
        setParentToBe,
        itemToDelete,
        setItemToDelete,
        changeParent,
        deleteItem,
        getItemLocation,
        getItemChildren,
        generateLocationStructure,
        itemToMove,
        setItemToMove,
        moveItem,
        upgradedItemFormOpen,
        setupgradedItemFormOpen,
        searchActive,
        setSearchActive,
        logoutUser,
        recalculateCurrentArray,
        itemSearchInput,
        setItemSearchInput,
        itemSearchValue,
        setItemSearchValue,
        locationSearchInput,
        setLocationSearchInput,
        locationSearchValue,
        setLocationSearchValue,
        searchOpen,
        setSearchOpen,
        popupMessage,
        setPopupMessage,
        displayNav,
        setDisplayNav,
        itemToMoveOrUse,
        setItemToMoveOrUse,
        SaveItem,
        searchItemsInUse,
        setSearchItemsInUse,
        CancelSearch,
        displayNavLinks,
        setDisplayNavLinks,
        isLoading,
        setIsLoading,
        itemsInView,
        setItemsInView,
        checkoutPopupOpen,
        setCheckoutPopupOpen,
        subscriptionExpired,
        setSubscriptionExpired,
        subscriptionEndDate,
        setSubscriptionEndDate,
        subscriptionStatus,
        setSubscriptionStatus,
        subscriptionCancelAtPeriodEnd,
        setSubscriptionCancelAtPeriodEnd,
        bulkWorkflow,
        setBulkWorkflow,
        regenerateCurrentArray,
        multiSelectMode,
        setMultiSelectMode,
        selectedItemsToMove,
        setSelectedItemsToMove,
        pendingQueueCount,
        offlineFailureCount,
        setOfflineFailureCount,
        offlineSyncedCount,
        setOfflineSyncedCount,
        pendingPreviews,
        syncAvailable,
        refreshFromRemote,
      }}
    >
      {props.children}
    </UserDataContext.Provider>
  );
};

function useUserDataContext() {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error("useUserContext must be used within UserContextProvider");
  }
  return context;
}

export { UserData, useUserDataContext };
